import { useSelector } from "react-redux";
import { selectInteractionDefinitions } from "./../../app/slices/InteractionPickerSlice";
import { InteractionDefintion } from "./../interaction_picker/InteractionDefinitions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  addInteractionCopy,
  selectSelectedTake3D,
  selectTrainingType,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import usePropHelper from "../../hooks/PropHelper";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import useInteractionPicker from "../../hooks/InteractionPicker.hook";
import { StringUtils } from "../utils/StringUtils";
import Interaction from "../../models/interactions/Interaction";

export interface InteractionContextMenuOptions {
  // when set, only these definitions are offered (e.g. quiz feedback subset)
  definitions?: InteractionDefintion[];
  // when set, the created interaction is handed here instead of the selected take
  onAddInteraction?: (interaction: Interaction) => void;
}

export default function useInteractionContextMenu(
  options: InteractionContextMenuOptions = {}
) {
  const { definitions, onAddInteraction } = options;
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [filter, setFilter] = useState<string>("");
  const defaultDefinitions = useSelector(selectInteractionDefinitions);
  const allDefinitions = definitions ?? defaultDefinitions;
  const [open, setOpen] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const trainingType = useSelector(selectTrainingType);
  const dispatch = useDispatch();
  const selectedTake = useSelector(selectSelectedTake3D);

  const { getInvolvedPropInteractions } = usePropHelper();
  const { getAllInvolvedSituationInteractions } = useSituationHelper();
  const { createNewInteraction } = useInteractionPicker();

  const selectPropInteractions = useCallback(() => {
    if (trainingType === TrainingType.Training360) return {};

    if (!selectedTake) return {};

    const defs = {
      ...getInvolvedPropInteractions(selectedTake),
      ...getAllInvolvedSituationInteractions(),
    };

    return defs;
  }, [
    trainingType,
    selectedTake,
    getInvolvedPropInteractions,
    getAllInvolvedSituationInteractions,
  ]);

  const filteredPropInteractions = useMemo(() => {
    const pi = selectPropInteractions();

    const filtered: Record<string, InteractionDefintion[]> = Object.fromEntries(
      Object.entries(pi)
        .map(([key, interactions]) => [
          key,
          interactions.filter((interaction) =>
            interaction.name.toLowerCase().includes(filter.toLowerCase())
          ),
        ])
        .filter(([, interactions]) => interactions.length > 0)
    );

    return filtered;
  }, [filter, selectPropInteractions]);

  const filteredDefinitions = useMemo(() => {
    // reset index if filter changes
    setSelectedIndex(0);

    // todo: re-format return type to also included prop instance id if it exists
    // todo: display the prop name next to the interaction name for clarity
    // prop interactions
    // filter logic
    // return allDefinitions.filter((def) =>
    //   def.name.toLowerCase().includes(filter.toLowerCase())
    // );

    const results: InteractionDefintion[] = [];

    for (const def of allDefinitions) {
      if (StringUtils.SubsequenceMatch(filter, def.name)) {
        results.push(def);
      }
    }

    return results;
  }, [filter, allDefinitions]);

  // register keystrokes when component mounts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;
      if (event.key === "ArrowUp") {
        incrementIndex(-1);
      } else if (event.key === "ArrowDown") {
        incrementIndex(1);
      } else if (event.key === "Enter") {
        // If Enter is pressed, select the current interaction
        if (filteredDefinitions[selectedIndex]) {
          onSelectInteraction(filteredDefinitions[selectedIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedIndex, open, filteredDefinitions]);

  const incrementIndex = (amount: number) => {
    setSelectedIndex((prev) => {
      const newIndex = prev + amount;
      if (newIndex < 0) return 0; // prevent going below 0
      if (newIndex >= filteredDefinitions.length)
        return filteredDefinitions.length - 1; // prevent going above the last index
      return newIndex;
    });
  };

  const onSelectInteraction = (interactionDefinition: InteractionDefintion) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    simulateEscapeKey();

    const { interaction, warnings } = {
      ...createNewInteraction(interactionDefinition),
    };

    if (!interaction) return;

    if (onAddInteraction) {
      // custom add target (e.g. quiz feedback slot)
      onAddInteraction(interaction);
    } else {
      dispatch(addInteractionCopy(interaction));
    }
  };

  const simulateEscapeKey = () => {
    const escapeEvent = new KeyboardEvent("keydown", {
      key: "Escape",
      bubbles: true, // Important: let the event bubble up the DOM
      cancelable: true,
    });
    document.dispatchEvent(escapeEvent);
  };

  const onOpenChange = (open: boolean) => {
    isSubmittingRef.current = false;
    setSelectedIndex(0);
    setFilter("");
    setOpen(open);
  };

  return {
    filteredDefinitions,
    onSelectInteraction,
    selectedIndex,
    setFilter,
    onOpenChange,
    filteredPropInteractions,
    // Closes the menu the same way a picked interaction does (synthetic
    // Escape); used by the fixed clipboard action rows.
    closeMenu: simulateEscapeKey,
  } as const;
}
